import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { ProductPickerItem } from '../../../Dashboard/_services/product.service';

type Segment = { text: string; match: boolean };

// Product search box shared by every "pick a product" field (purchase
// orders, stock scrap, delivery validation). The list sits in an overlay so
// it's never clipped by a scrolling table, works with ↑ ↓ Enter / Esc, matches
// every typed word in any order across name, SKU and barcode, ranks the best
// matches first and highlights what matched.
//
// The text in the box is two-way bound through [(query)] - callers that
// allow free text (a PO line description) keep whatever was typed, and
// (queryEdited) fires only when the user changes it, so a stale selection
// can be cleared.
@Component({
    selector: 'app-product-picker',
    imports: [MatAutocompleteModule, MatIconModule],
    templateUrl: './product-picker.component.html',
    styleUrl: './product-picker.component.css',
    encapsulation: ViewEncapsulation.None
})
export class ProductPickerComponent implements OnChanges {
  @Input() products: ProductPickerItem[] = [];
  @Input() query = '';
  @Input() placeholder = 'Search by name, SKU or barcode';
  // 'sm' fits a table cell, 'md' is a standalone form field.
  @Input() size: 'sm' | 'md' = 'md';
  // The product currently linked to this field, for the tick in the box
  // and on its row in the list.
  @Input() selectedId: number | null = null;
  // List everything on focus before anything is typed.
  @Input() showAllOnEmpty = true;
  // Listed instead when the box is empty (e.g. products like the one being
  // replaced), under this heading.
  @Input() suggestions: ProductPickerItem[] = [];
  @Input() suggestionsLabel = 'Suggestions';
  // Only products with a variant (needed when the pick becomes a stock line).
  @Input() variantOnly = false;
  // Search only what's after the last comma (multi-value filter boxes).
  @Input() commaSeparated = false;
  @Input() showStock = true;
  // Count stock at one yard only ('M' = Markstey); null shows both.
  @Input() stockYard: 'K' | 'M' | null = null;
  // Shown when nothing matches, e.g. that the text is kept as a description.
  @Input() noMatchHint = 'Try fewer words, or search by SKU';
  // Enter picks the highlighted row; switch off where Enter should submit
  // the typed text instead (filters), leaving ↑ ↓ to pick.
  @Input() autoActiveFirst = true;
  @Input() panelWidth: string | number = '';
  @Input() maxResults = 30;

  @Output() queryChange = new EventEmitter<string>();
  @Output() queryEdited = new EventEmitter<string>();
  @Output() picked = new EventEmitter<ProductPickerItem>();
  @Output() focused = new EventEmitter<void>();
  // Enter pressed without picking a suggestion.
  @Output() enterKey = new EventEmitter<void>();

  @ViewChild(MatAutocompleteTrigger) trigger?: MatAutocompleteTrigger;

  results: ProductPickerItem[] = [];
  total = 0;
  term = '';
  private pickedOnThisKey = false;

  ngOnChanges(): void {
    this.refresh();
  }

  get isLinked(): boolean {
    return this.selectedId != null && !!this.query.trim();
  }

  // The trigger writes this into the box after a pick.
  displayWith = (value: ProductPickerItem | string | null): string =>
    typeof value === 'string' ? value : value?.productName ?? '';

  onInput(value: string): void {
    this.query = value;
    this.queryChange.emit(value);
    this.queryEdited.emit(value);
    this.refresh();
  }

  onPicked(product: ProductPickerItem): void {
    this.pickedOnThisKey = true;
    let next = product.productName;
    if (this.commaSeparated) {
      const terms = this.query
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      terms.pop(); // the in-progress term the suggestion replaces
      if (!terms.includes(product.productName)) terms.push(product.productName);
      next = terms.join(', ');
    }
    this.query = next;
    this.queryChange.emit(next);
    this.picked.emit(product);
    this.refresh();
  }

  onEnterUp(): void {
    if (!this.pickedOnThisKey) this.enterKey.emit();
    this.pickedOnThisKey = false;
  }

  clear(): void {
    this.onInput('');
    // Reopen so the next search can start straight away.
    setTimeout(() => this.trigger?.openPanel());
  }

  totalStock(p: ProductPickerItem): number {
    if (this.stockYard === 'M') return p.mStockQty ?? 0;
    if (this.stockYard === 'K') return p.kStockQty ?? 0;
    return (p.kStockQty ?? 0) + (p.mStockQty ?? 0);
  }

  segments(text: string | null | undefined): Segment[] {
    const value = String(text ?? '');
    const terms = this.term.split(/\s+/).filter(Boolean);
    if (!terms.length || !value) return [{ text: value, match: false }];
    const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const re = new RegExp(`(${escaped.join('|')})`, 'ig');
    return value
      .split(re)
      .filter((part) => part !== '')
      .map((part) => ({ text: part, match: terms.includes(part.toLowerCase()) }));
  }

  private currentTerm(): string {
    const text = this.query || '';
    const last = this.commaSeparated ? text.split(',').pop() ?? '' : text;
    return last.trim().toLowerCase();
  }

  private refresh(): void {
    const pool = this.variantOnly ? (this.products || []).filter((p) => p.productVariantId) : this.products || [];
    this.term = this.currentTerm();
    const terms = this.term.split(/\s+/).filter(Boolean);
    if (!terms.length) {
      const all = this.suggestions?.length ? this.suggestions : this.showAllOnEmpty ? pool : [];
      this.results = all.slice(0, this.maxResults);
      this.total = all.length;
      return;
    }
    const q = terms.join(' ');
    const scored: { p: ProductPickerItem; score: number }[] = [];
    for (const p of pool) {
      const fields = [p.productName, p.sku, p.barcode].map((f) => (f || '').toLowerCase());
      const haystack = fields.join(' ');
      if (!terms.every((t) => haystack.includes(t))) continue;
      let score = 0;
      if (fields.some((f) => f === q)) score += 100; // exact name / SKU / barcode
      if (fields.some((f) => f.startsWith(q))) score += 50;
      for (const t of terms) {
        if (fields.some((f) => f.split(/[\s\-_/]+/).some((w) => w.startsWith(t)))) score += 5;
      }
      scored.push({ p, score });
    }
    scored.sort((a, b) => b.score - a.score); // stable: ties keep list order
    this.results = scored.slice(0, this.maxResults).map((s) => s.p);
    this.total = scored.length;
  }
}
